import api from "../axios";

// Tenant Analytics Service Class
export class TenantAnalyticsService {
  // Overview endpoints
  async getTenantOverview(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/overview`
    );
    return response.data;
  }
  // {{baseURL}}/tenant-analytics/{{tenantId}}/{{tenantServiceId}}/{{tenantServiceOptionId}}/overview/key-milestones
  async getTenantOverviewKeyMilestones(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/overview/key-milestones`
    );
    return response.data;
  }

  async getTenantManagement(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/overview/management`
    );
    return response.data;
  }

  async getTenantFinancialsOverview(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/overview/financials-overview`
    );
    return response.data;
  }

  async getTenantKeyMilestones(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/overview/key-milestones`
    );
    return response.data;
  }

  // Credit Analysis specific endpoints
  async getCreditAnalysisBasicCorporateDetails(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis`
    );
    return response.data;
  }

  async getCreditAnalysisFinancialProfile(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/financial-profile`
    );
    return response.data;
  }

  async getCreditAnalysisCreditRiskSpecificData(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/credit-risk-specific-data`
    );
    return response.data;
  }

  async getCreditAnalysisPortfolioReview(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/portfolio-review-management-information`
    );
    return response.data;
  }

  async getCreditAnalysisProductLevelInformation(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/product-level-information`
    );
    return response.data;
  }

  async getCreditAnalysisAssessmentData(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/credit-assessment-data`
    );
    return response.data;
  }

  async getCreditAnalysisAdditionalData(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/additional-data`
    );
    return response.data;
  }

  async getCreditAnalysisESGAnalysis(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/credit-analysis/esg-analysis`
    );
    return response.data;
  }

  // Financials endpoints
  async getTenantFinancials(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/financials`
    );
    return response.data;
  }

  async getCompanyData(name: string) {
    const response = await api.get(`privco/search/${encodeURIComponent(name)}`);
    return response.data;
  }
  async getCompanyFinancialData(profileId: number) {
    const response = await api.get(`/privco/financials/company/${profileId}`);
    return response.data;
  }

  async getTenantRevenue(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/financials/revenue`
    );
    return response.data;
  }

  async getTenantProfitability(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/financials/profitability`
    );
    return response.data;
  }

  async getTenantKeyRatios(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/financials/key-ratios`
    );
    return response.data;
  }

  // Brand Trust endpoints
  async getTenantBrandTrust(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/brand-trust`
    );
    return response.data;
  }
  // {{baseURL}}/tenant-analytics/{{tenantId}}/{{tenantServiceId}}/{{tenantServiceOptionId}}/brand-trust/online-presence
  async getTenantOnlinePresence(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/brand-trust/online-presence`
    );
    return response.data;
  }

  async getClientReviews(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/brand-trust/client-reviews`
    );
    return response.data;
  }

  async getEmployeeReviews(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/brand-trust/employee-reviews`
    );
    return response.data;
  }
  // {{baseURL}}/tenant-analytics/{{tenantId}}/{{tenantServiceId}}/{{tenantServiceOptionId}}/brand-trust/news
  async getNewsFeed(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/brand-trust/news`
    );
    return response.data;
  }
  // {{baseURL}}/tenant-analytics/{{tenantId}}/{{tenantServiceId}}/{{tenantServiceOptionId}}/opportunities/growth-opportunities
  async getGrowthOpportunities(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/opportunities/growth-opportunities`
    );
    return response.data;
  }
  // {{baseURL}}/tenant-analytics/{{tenantId}}/{{tenantServiceId}}/{{tenantServiceOptionId}}/opportunities/tailwinds-and-headwinds
  async getTailwindsAndHeadwinds(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/opportunities/tailwinds-and-headwinds`
    );
    return response.data;
  }

  async getTenantTrustScore(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/brand-trust/trust-score`
    );
    return response.data;
  }

  // Competitive Landscape endpoints
  async getTenantCompetitiveLandscape(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/competitive-landscape`
    );
    return response.data;
  }

  async getTenantCompetitiveLandscapeMap(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/competitive-landscape/map`
    );
    return response.data;
  }
  // async getTenantMarketPositioning(
  //   tenantId: string,
  //   tenantServiceId: string,
  //   tenantServiceOptionId: string
  // ) {
  //   const response = await api.get(
  //     `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/competitive-landscape/market-positioning`
  //   );
  //   return response.data;
  // }

  // async getTenantCompetitors(
  //   tenantId: string,
  //   tenantServiceId: string,
  //   tenantServiceOptionId: string
  // ) {
  //   const response = await api.get(
  //     `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/competitive-landscape/competitors`
  //   );
  //   return response.data;
  // }

  // Opportunities endpoints
  async getTenantOpportunities(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/opportunities`
    );
    return response.data;
  }

  async getTenantMarketOpportunities(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/opportunities/market-opportunities`
    );
    return response.data;
  }

  async getTenantStrategicOpportunities(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/opportunities/strategic-opportunities`
    );
    return response.data;
  }

  // Shareholders endpoints
  async getTenantShareholders(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/shareholders`
    );
    return response.data;
  }

  async getPreviousOwners(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/shareholders/previous-owners`
    );
    return response.data;
  }

  async getTenantGovernance(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/shareholders/governance`
    );
    return response.data;
  }

  async getTenantRecentChanges(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/shareholders/recent-changes`
    );
    return response.data;
  }

  async getTenantRiskScorecard(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/risk-scorecard`
    );
    return response.data;
  }

  async getTenantOverallScore(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/risk-scorecard/overall-score`
    );
    return response.data;
  }

  async getTenantRiskCategories(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/risk-scorecard/risk-categories`
    );
    return response.data;
  }

  async getTenantRiskMitigation(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/risk-scorecard/mitigation`
    );
    return response.data;
  }
  async getTenantRiskFactors(
    tenantId: string,
  ) {
    const response = await api.get(
      `/tenant-risk-factors`,{
    params: { tenantId }
  }
    );
    return response.data;
  }

  async autoCreateTenantRiskFactors(tenantId: string) {
    const response = await api.post(
      `/factor-groups/auto-create-tenant`,
      { tenantId }
    );
    return response.data;
  }

  async getRealEstateParallelAnalysis(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string,    
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/real-estate/analytics/parallel`
    );
    return response.data;
  }

  // KYC & KUB endpoints
  async getKycKubMlQueries(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string,
    params?: {
      status?: string;
      type?: string;
      limit?: number;
    }
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/kyc-kubkyc-kub/ml-queries`,
      { params }
    );
    return response.data;
  }

  async getKycKubMlQueriesStatistics(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/kyc-kubkyc-kub/ml-queries/statistics`
    );
    return response.data;
  }
    // ML Queries endpoints (generic for all categories)
  async getMlQueries(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string,
    params?: {
      status?: string;
      type?: string;
      category?: 'KYB' | 'RISK_PARAMETERS' |'BANK_RISK_SCORE'
      limit?: number;
    }
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/kyc-kub/ml-queries`,
      { params }
    );
    return response.data;
  }

  async getMlQueriesStatistics(
    tenantId: string,
    tenantServiceId: string,
    tenantServiceOptionId: string,
    category?: 'KYB' | 'RISK_PARAMETERS'  | 'BANK_RISK_SCORE'
  ) {
    const response = await api.get(
      `/tenant-analytics/${tenantId}/${tenantServiceId}/${tenantServiceOptionId}/kyc-kub/ml-queries/statistics`,
      { params: category ? { category } : undefined }
    );
    return response.data;
  }
}


// Create and export singleton instance
export const tenantAnalyticsService = new TenantAnalyticsService();

// Export individual methods for convenience
export const {
  getTenantOverview,
  getTenantManagement,
  getTenantFinancialsOverview,
  getTenantKeyMilestones,
  // Credit Analysis specific methods
  getCreditAnalysisBasicCorporateDetails,
  getCreditAnalysisFinancialProfile,
  getCreditAnalysisCreditRiskSpecificData,
  getCreditAnalysisPortfolioReview,
  getCreditAnalysisProductLevelInformation,
  getCreditAnalysisAssessmentData,
  getCreditAnalysisAdditionalData,
  getCreditAnalysisESGAnalysis,
  // Other methods
  getTenantFinancials,
  getCompanyData,
  getCompanyFinancialData,
  getTenantRevenue,
  getTenantProfitability,
  getTenantKeyRatios,
  getTenantBrandTrust,
  getTenantTrustScore,
  getTenantOnlinePresence,
  getTenantCompetitiveLandscape,
  getTenantCompetitiveLandscapeMap,
  getTenantOpportunities,
  getTenantMarketOpportunities,
  getTenantStrategicOpportunities,
  getTenantShareholders,
  getClientReviews,
  getEmployeeReviews,
  getNewsFeed,
  getGrowthOpportunities,
  getTailwindsAndHeadwinds,
  getPreviousOwners,
  getTenantGovernance,
  getTenantRecentChanges,
  getTenantOverviewKeyMilestones,
  getTenantRiskScorecard,
  getTenantOverallScore,
  getTenantRiskCategories,
  getTenantRiskMitigation,
  getTenantRiskFactors,
  autoCreateTenantRiskFactors,
  getRealEstateParallelAnalysis,
  getKycKubMlQueries,
  getKycKubMlQueriesStatistics,
  getMlQueries,
  getMlQueriesStatistics
} = tenantAnalyticsService;
